import PropTypes from 'prop-types';
import React from 'react';
import { Set } from 'immutable';
import Col from 'react-bootstrap/lib/Col';
import Row from 'react-bootstrap/lib/Row';
import FormGroup from 'react-bootstrap/lib/FormGroup';

import { translate as t } from '../../lib/I18n';
import config from '../../config';
import TrackingActions from "../../actions/TrackingActions";

import SeasonSearch from '../../records/SeasonSearch';
import SectionErrors from '../search/SectionErrors';
import FeatureFlag from '../general/FeatureFlag';
import Checkbox from '../form/Checkbox';
import HelpIcon from '../general/HelpIcon';
import FormattedHTMLMessage from '../general/FormattedHTMLMessage';
import ExternalLink from '../general/ExternalLink';

class SeasonDuration extends React.PureComponent {

    static propTypes = {
        search: PropTypes.instanceOf(SeasonSearch).isRequired,
        onChange: PropTypes.func,
        searchErrors: PropTypes.instanceOf(Set).isRequired,
        searchAttempted: PropTypes.bool
    };

    _handleToggleFor = (name) => (event) => {
        // NOTE: DO NOT CHANGE THE NAME OF THE EVENTS
        TrackingActions.widgetSearchClick({ name: `${name}:${event.target.checked}` });
        const value = event.target.checked;
        const search = this.props.search.set(name, value);
        this.props.onChange(search);
    };

    componentDidUpdate() {
        const search = this.props.search;
        if (search.passengerType !== 'adult' && search.flexi) {
            const updatedSearch = this.props.search.set('flexi', false);
            this.props.onChange(updatedSearch);
        }
    }

    renderLabelWithHelpIcon ({
        labelText,
        supplementaryLabel,
        ariaLabelText,
        modalTitle,
        modalMessage,
        moreInfoText,
        buttonClassName,
    }) {
        return (
            <span>
                <span className="sr-only">{ labelText }</span>
                <span aria-hidden="true">{ ariaLabelText }</span>
                {supplementaryLabel}
                <HelpIcon
                    icon={ 'info' }
                    heading={ modalTitle }
                    message={ modalMessage }
                    moreInfoText={ moreInfoText }
                    buttonClassName={ buttonClassName }
                />
            </span>
        );
    }

    render () {
        const { search, searchAttempted, searchErrors } = this.props;

        let error;
        if (searchAttempted && searchErrors.has('validateHasDuration')) {
            error = 'booking.season.errors.durationMissing';
        }

        const { passengerType } = search;

        return (
            <div className="datetime__season-duration">
                <Row>
                    <FormGroup className="form--vertical">
                        <fieldset className="hidden-fieldset">
                            <legend className="sr-only">{ t('season.options.duration') }</legend>

                            <FeatureFlag name="enableFlexiSeasonTickets">
                                <Col xs={ 6 }>
                                    <Checkbox
                                        className="flexi-duration-checkbox" name="flexi"
                                        labelClassName="flexi"
                                        label={ this.renderLabelWithHelpIcon({
                                            labelText: t('ariaLabel.seasonDuration', { duration: 'flexi' }),
                                            ariaLabelText: t('season.options.labels.flexi'),
                                            modalTitle: t('help.seasonTickets.flexi.title'),
                                            modalMessage: t('help.seasonTickets.flexi.message'),
                                            moreInfoText: (
                                                <FormattedHTMLMessage
                                                    message="help.seasonTickets.flexi.moreInfo"
                                                    moreInfoLink={
                                                        <ExternalLink
                                                            message={ t('help.seasonTickets.flexi.moreInfo.link.text') }
                                                            href={ config.get('flexiHelpUrl') }
                                                            aria-label={ t('help.seasonTickets.flexi.moreInfo.link.aria') }
                                                        />
                                                    }
                                                />
                                            ),
                                            buttonClassName: "flexi-duration-checkbox__help-icon",
                                        }) }
                                        value="flexi"
                                        checked={ passengerType === 'adult' && search.flexi }
                                        onChange={ this._handleToggleFor('flexi') }
                                        disabled={ passengerType !== 'adult' }
                                        error={ error }
                                    />
                                </Col>
                            </FeatureFlag>

                            <FeatureFlag name="enableWeeklySeasonTickets">
                                <Col xs={ 6 } >
                                    <Checkbox
                                        name="weekly"
                                        labelClassName="weekly"
                                        label={ t('season.options.labels.weekly') }
                                        value="weekly"
                                        checked={ search.weekly }
                                        onChange={ this._handleToggleFor('weekly') }
                                        srOnly={ t('ariaLabel.seasonDuration', { duration: 'weekly' }) }
                                        error={ error }
                                    />
                                </Col>
                            </FeatureFlag>

                            <FeatureFlag name="enableFourWeeklySeasonTickets" disabled={ !search.smartcard }>
                                <Col xs={ 6 }>
                                    <Checkbox
                                        name="fourWeekly"
                                        labelClassName="fourWeekly"
                                        label={ t('season.options.labels.fourWeekly') }
                                        value="fourWeekly"
                                        checked={ search.fourWeekly }
                                        onChange={ this._handleToggleFor('fourWeekly') }
                                        srOnly={ t('ariaLabel.seasonDuration', { duration: 'four weekly' }) }
                                        error={ error }
                                    />
                                </Col>
                            </FeatureFlag>

                            <Col xs={ 6 }>
                                <Checkbox
                                    name="monthly"
                                    labelClassName="monthly"
                                    label={ t('season.options.labels.monthly') }
                                    value="monthly"
                                    checked={ search.monthly }
                                    onChange={ this._handleToggleFor('monthly') }
                                    srOnly={ t('ariaLabel.seasonDuration', { duration: 'monthly' }) }
                                    error={ error }
                                />
                            </Col>

                            <FeatureFlag name="enableQuarterlySeasonTickets">
                                <Col xs={ 6 }>
                                    <Checkbox
                                        name="quarterly"
                                        labelClassName="quarterly"
                                        label={ t('season.options.labels.quarterly') }
                                        value="weekly"
                                        checked={ search.quarterly }
                                        onChange={ this._handleToggleFor('quarterly') }
                                        srOnly={ t('ariaLabel.seasonDuration', { duration: 'quarterly' }) }
                                        error={ error }
                                    />
                                </Col>
                            </FeatureFlag>

                            <FeatureFlag name="enableSixMonthlySeasonTickets">
                                <Col xs={ 6 }>
                                    <Checkbox
                                        name="sixMonthly"
                                        labelClassName="sixMonthly"
                                        label={ t('season.options.labels.sixMonthly') }
                                        value="weekly"
                                        checked={ search.sixMonthly }
                                        onChange={ this._handleToggleFor('sixMonthly') }
                                        srOnly={ t('ariaLabel.seasonDuration', { duration: 'six monthly' }) }
                                        error={ error }
                                    />
                                </Col>
                            </FeatureFlag>

                            <Col xs={ 6 } >
                                <Checkbox
                                    name="annual"
                                    labelClassName="annual"
                                    label={ t('season.options.labels.annual') }
                                    value="annual"
                                    checked={ search.annual }
                                    onChange={ this._handleToggleFor('annual') }
                                    srOnly={ t('ariaLabel.seasonDuration', { duration: 'annual' }) }
                                    error={ error }
                                    disabled={ !search.isAnnualAvailable() }
                                />
                            </Col>
                            <Col xs={ 6 }>
                                <Checkbox
                                    name="custom"
                                    labelClassName="custom"
                                    label={ this.renderLabelWithHelpIcon({
                                        labelText: t('ariaLabel.seasonDuration', { duration: 'custom' }),
                                        ariaLabelText: t('season.options.labels.custom'),
                                        modalTitle: t('help.seasonTickets.custom.title'),
                                        modalMessage: t('help.seasonTickets.custom.message')
                                    }) }
                                    value="custom"
                                    checked={ search.custom }
                                    onChange={ this._handleToggleFor('custom') }
                                    error={ error }
                                />
                            </Col>
                        </fieldset>
                    </FormGroup>
                </Row>
                <SectionErrors errors={ error ? [error] : [] } />
            </div>
        );
    }
}

export default SeasonDuration;



// WEBPACK FOOTER //
// ./src/components/seasonTicket/SeasonDuration.js